import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Ion } from "@/components/Ion";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import LogTestFromChat from "@/components/laser/LogTestFromChat";

// 1:1 port of frontend/src/components/CleaningSettingsCard.tsx
const PATTERN_LABEL = { line: "Line", rectangle: "Rectangle", round: "Round", sine_line: "Sine Line", spiral: "Spiral", lissajous: "Lissajous", flower: "Flower", honeycomb: "Honeycomb" };
const PATTERN_FALLBACK = { honeycomb: "lissajous", zigzag: "sine_line" };

function normalizeSubstrate(cat) {
  if (!cat) return "other";
  const c = cat.toLowerCase();
  if (["wood", "wooden", "oak", "pine", "lumber"].some((k) => c.includes(k))) return "wood";
  if (["stone", "concrete", "brick", "masonry"].some((k) => c.includes(k))) return "stone_concrete";
  if (["steel", "aluminum", "metal", "iron", "alloy", "carbon"].some((k) => c.includes(k))) return "metal";
  return "other";
}
function normalizePattern(p) {
  if (!p) return "rectangle";
  const k = p.toLowerCase();
  return PATTERN_LABEL[k] ? k : PATTERN_FALLBACK[k] || "rectangle";
}
const num = (v) => typeof v === "number";

export const Param = ({ label, value }) => (
  <div data-testid={`param-${label}`} style={{ flexBasis: "22%", flexGrow: 1, minWidth: 92, padding: 10, borderRadius: 10, background: "#0A0A0A", border: "1px solid #333333" }}>
    <p style={{ color: "#A0A0A0", fontSize: 9, fontWeight: 800, letterSpacing: 1.2, marginBottom: 4 }}>{label}</p>
    <p style={{ color: "#FFFFFF", fontSize: 15, fontWeight: 700 }}>{value}</p>
  </div>
);

export default function CleaningSettingsCard({ data }) {
  const { user } = useAuth();
  const [saveState, setSaveState] = useState("idle");
  const [errorMsg, setErrorMsg] = useState(null);
  const canSave = !!user && ["franchisee", "franchisee_employee", "admin"].includes(user.role || "");
  const titleBits = [data.substrate_detail, data.contaminant].filter(Boolean);
  const title = titleBits.length ? titleBits.join(" + ") : "Cleaning Settings";
  const patternLabel = data.pattern ? PATTERN_LABEL[data.pattern] || data.pattern : null;
  const patternDisplay = patternLabel ? (num(data.pattern_size_mm) ? `${patternLabel} Ø${data.pattern_size_mm}mm` : patternLabel) : null;
  const hatchDisplay = num(data.hatch_1_mm) ? (data.hatch_2_mm != null ? `${data.hatch_1_mm} × ${data.hatch_2_mm} mm` : `${data.hatch_1_mm} mm`) : null;

  const params = [];
  if (num(data.power_percent)) params.push(["POWER", `${data.power_percent}%`]);
  if (num(data.frequency_khz)) params.push(["FREQ", `${data.frequency_khz} kHz`]);
  if (num(data.pulse_width_ns)) params.push(["PULSE", `${data.pulse_width_ns} ns`]);
  if (num(data.speed_mm_s)) params.push(["SPEED", `${data.speed_mm_s.toLocaleString()} mm/s`]);
  if (patternDisplay) params.push(["PATTERN", patternDisplay]);
  if (hatchDisplay) params.push(["HATCH", hatchDisplay]);
  if (num(data.metal_thickness_mm)) params.push(["THICKNESS", `${data.metal_thickness_mm} mm`]);
  if (num(data.focal_distance_mm)) params.push(["FOCAL", `${data.focal_distance_mm} mm`]);
  if (num(data.passes)) params.push(["PASSES", `${data.passes}`]);

  const handleSave = async () => {
    if (saveState === "saving") return;
    setSaveState("saving"); setErrorMsg(null);
    try {
      const substrate_category = normalizeSubstrate(data.substrate_category);
      const payload = {
        laser_model: data.laser_model || "500W", substrate_category,
        substrate_detail: (data.substrate_detail || "AI Recipe").slice(0, 80), contaminant: (data.contaminant || "Unspecified").slice(0, 60),
        pattern: normalizePattern(data.pattern),
        power_percent: num(data.power_percent) ? data.power_percent : 50, frequency_khz: num(data.frequency_khz) ? data.frequency_khz : 50,
        pulse_width_ns: num(data.pulse_width_ns) ? data.pulse_width_ns : 200, speed_mm_s: num(data.speed_mm_s) ? data.speed_mm_s : 3000,
        pattern_size_mm: num(data.pattern_size_mm) ? data.pattern_size_mm : 4, hatch_1_mm: num(data.hatch_1_mm) ? data.hatch_1_mm : 0.1,
        notes: data.notes || "Saved from AI Cleaning Agent recommendation.",
      };
      if (num(data.hatch_2_mm)) payload.hatch_2_mm = data.hatch_2_mm;
      if (substrate_category === "metal") payload.metal_thickness_mm = num(data.metal_thickness_mm) ? data.metal_thickness_mm : 6;
      await api("/ai/training/setting", { method: "POST", body: payload });
      setSaveState("saved");
    } catch (e) { setErrorMsg(e.message || "Couldn't save — try again."); setSaveState("error"); }
  };

  const err = saveState === "error";
  return (
    <div data-testid="cleaning-settings-card" style={{ background: "#121212", borderRadius: 14, border: "1px solid #333333", padding: 14, margin: "4px 0", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {data.laser_model && (
          <span data-testid="cleaning-settings-model-badge" style={{ padding: "5px 10px", background: "rgba(76, 204, 6,0.08)", borderRadius: 8, border: "1.5px solid #4CCC06", color: "#4CCC06", fontSize: 12, fontWeight: 900, letterSpacing: 0.6 }}>{data.laser_model}</span>
        )}
        <p data-testid="cleaning-settings-title" className="line-clamp-2" style={{ flex: 1, minWidth: 0, color: "#FFFFFF", fontSize: 16, fontWeight: 700 }}>{title}</p>
        {canSave && <LogTestFromChat data={data} substrate={normalizeSubstrate(data.substrate_category)} pattern={normalizePattern(data.pattern)} />}
        {canSave && (
          <button onClick={handleSave} disabled={saveState === "saving" || saveState === "saved"} data-testid="cleaning-settings-save-btn" aria-label="Save settings to my library"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, padding: "6px 10px", borderRadius: 8, minWidth: 64, border: `1px solid ${err ? "#FF6B6B" : "#4CCC06"}`, background: err ? "rgba(255,107,107,0.10)" : saveState === "saved" ? "rgba(76, 204, 6,0.18)" : "rgba(76, 204, 6,0.06)" }}>
            {saveState === "saving" ? <Loader2 size={16} className="animate-spin" color="#4CCC06" /> : saveState === "saved" ? <Ion name="checkmark" size={16} color="#4CCC06" /> : err ? <Ion name="alert-circle-outline" size={16} color="#FF6B6B" /> : <Ion name="bookmark-outline" size={16} color="#4CCC06" />}
            <span style={{ color: err ? "#FF6B6B" : "#4CCC06", fontSize: 11, fontWeight: 900, letterSpacing: 0.8 }}>{saveState === "saved" ? "SAVED" : err ? "RETRY" : saveState === "saving" ? "" : "SAVE"}</span>
          </button>
        )}
      </div>
      {params.length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{params.map(([l, v]) => <Param key={l} label={l} value={v} />)}</div>}
      {data.notes && <p className="line-clamp-4" style={{ color: "#A0A0A0", fontSize: 13, lineHeight: "18px", fontStyle: "italic" }}>{`"${data.notes}"`}</p>}
      {err && errorMsg && <p style={{ color: "#FF6B6B", fontSize: 11, marginTop: 4 }}>{errorMsg}</p>}
    </div>
  );
}
